export type WaitlistEntriesSortColumn = 'startTime' | 'dateCreated' | 'numberOfTimesPrompted' | 'dateLastPrompted'
export type WaitlistEntriesSortDirection = 'asc' | 'desc'

interface SortableWaitlistEntry {
    dateTime: Date
    dateCreated: Date
    numberOfTimesPrompted: number
    dateLastPrompted: Date | null
}

const SORT_ICONS = {
    activeDown: 'assets/caret/down.svg',
    activeUp: 'assets/caret/normal.svg',
    inactive: 'assets/dropdown_arrows.svg',
} as const

const DEFAULT_DIRECTIONS: Record<WaitlistEntriesSortColumn, WaitlistEntriesSortDirection> = {
    startTime: 'asc',
    dateCreated: 'desc',
    numberOfTimesPrompted: 'desc',
    dateLastPrompted: 'desc',
}

export class WaitlistEntriesSort {

    readonly column: WaitlistEntriesSortColumn
    readonly direction: WaitlistEntriesSortDirection
    readonly isDefault: boolean

    constructor(column: WaitlistEntriesSortColumn = 'startTime', direction?: WaitlistEntriesSortDirection, isDefault = false) {
        this.column = column
        this.direction = direction ?? DEFAULT_DIRECTIONS[column]
        this.isDefault = isDefault
    }

    static default(): WaitlistEntriesSort {
        return new WaitlistEntriesSort('startTime', 'asc', true)
    }

    toggle(column: WaitlistEntriesSortColumn): WaitlistEntriesSort {
        if (this.isDefault || this.column !== column) {
            return new WaitlistEntriesSort(column)
        }
        const defaultDir = DEFAULT_DIRECTIONS[column]
        if (this.direction === defaultDir) {
            const flipped = defaultDir === 'asc' ? 'desc' : 'asc'
            return new WaitlistEntriesSort(column, flipped)
        }
        return WaitlistEntriesSort.default()
    }

    isActive(column: WaitlistEntriesSortColumn): boolean {
        return !this.isDefault && this.column === column
    }

    icon(column: WaitlistEntriesSortColumn): string {
        if (!this.isActive(column)) {
            return SORT_ICONS.inactive
        }
        return this.direction === 'asc' ? SORT_ICONS.activeUp : SORT_ICONS.activeDown
    }

    iconAlt(column: WaitlistEntriesSortColumn): string {
        if (!this.isActive(column)) {
            return 'Sort'
        }
        return this.direction === 'asc' ? 'Sorted ascending' : 'Sorted descending'
    }

    isIconInactive(column: WaitlistEntriesSortColumn): boolean {
        return !this.isActive(column)
    }

    apply<T extends SortableWaitlistEntry>(entries: T[]): T[] {
        const sortedEntries = [...entries]
        const dir = this.direction === 'asc' ? 1 : -1
        if (this.column === 'dateCreated') {
            sortedEntries.sort((a, b) => dir * (a.dateCreated.getTime() - b.dateCreated.getTime()))
        } else if (this.column === 'numberOfTimesPrompted') {
            sortedEntries.sort((a, b) => dir * (a.numberOfTimesPrompted - b.numberOfTimesPrompted))
        } else if (this.column === 'dateLastPrompted') {
            sortedEntries.sort((a, b) => dir * ((a.dateLastPrompted?.getTime() ?? 0) - (b.dateLastPrompted?.getTime() ?? 0)))
        } else {
            sortedEntries.sort((a, b) => dir * (a.dateTime.getTime() - b.dateTime.getTime()))
        }
        return sortedEntries
    }
}
